/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/nav/menu.ts (v2 com T-codes)
 * ----------------------------------------------------------------------------
 * VERSÃO PREVIEW DEMO 2026-05-21
 *
 * Mudanças vs v1:
 * - Adicionado campo `tcode?: string` em MenuItem para mostrar T-code técnico
 *   (SAP-style: SC02, MV05, etc.) ao lado do label.
 * - Mantém TUDO o resto inalterado: paths, activeMatch, roles, hierarquia.
 *
 * Story para demo:
 *   "Cada módulo, cada sub-módulo, tem o seu código técnico para
 *    rastreabilidade total. É o rigor industrial que vocês conhecem,
 *    com a agilidade de software moderno."
 * ========================================================================== */

import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  ScanLine,
  Printer,
  Package,
  Boxes,
  Building2,
  Users,
  Layers,
  MapPin,
  Truck,
  Factory,
  Wrench,
  Smartphone,
  Receipt,
  ListChecks,
  ShoppingCart,
  List,
  Inbox,
  Settings,
  Calendar,
  AlertTriangle,
  Megaphone,
  TrendingUp,
  FlaskConical,
  Cpu,
  Network,
  Activity,
  Plug,
  Globe,
  Radio,
  Bot,
  Tag,
  Camera,
  History,
  Database,
  HeartPulse,
  Code2,
  Barcode,
} from 'lucide-react'

/** Estrutura básica de cada item do menu. */
export type MenuItem = {
  id: string
  label: string
  /** Código técnico (T-code SAP-style) — mostrado em monospace */
  tcode?: string
  path?: string
  href?: string
  target?: string
  icon?: LucideIcon
  children?: MenuItem[]
  roles?: string[]
  activeMatch?: RegExp | RegExp[]
  /** ADR-0042: visivel so a superusuarios (gate de UX; a seguranca real e' o backend). */
  superuserOnly?: boolean
}

export const MENUS: MenuItem[] = [
  // ============================== INDICADORES ==============================
  {
    id: 'indicadores',
    label: 'INDICADORES',
    children: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/',
        icon: LayoutDashboard,
        tcode: 'IN01',
        activeMatch: [/^\/$/],
      },
      {
        id: 'wf-dashboard',
        label: 'Dashboard Workforce',
        path: '/workforce/dashboard',
        icon: LayoutDashboard,
        tcode: 'IN02',
        activeMatch: /^\/workforce\/dashboard(\/|$)/,
      },
    ],
  },

  // ============================== FERRAMENTAS =============================
  {
    id: 'ferramentas',
    label: 'FERRAMENTAS',
    children: [
      {
        id: 'scan',
        label: 'Scan',
        path: '/inventory/scan',
        icon: ScanLine,
        tcode: 'TL01',
        activeMatch: [/^\/inventory\/scan(\/|$)/, /^\/scan(\/|$)/],
      },
      {
        id: 'consulta-estoque',
        label: 'Consulta de Estoque',
        path: '/inventory/search',
        icon: Boxes,
        tcode: 'TL02',
        activeMatch: [/^\/inventory\/search(\/|$)/],
      },
      {
        id: 'impressao',
        label: 'Impressão',
        path: '/printing',
        icon: Printer,
        tcode: 'TL03',
        activeMatch: [/^\/printing$/, /^\/printing\/(?!printers)/],
      },
      {
        id: 'impressoras',
        label: 'Impressoras',
        path: '/printing/printers',
        icon: Printer,
        tcode: 'TL04',
        activeMatch: [/^\/printing\/printers(\/|$)/],
      },
    ],
  },

  // ============================== OPERAÇÕES ==============================
  {
    id: 'operacoes',
    label: 'OPERAÇÕES',
    children: [
      {
        id: 'recebimento',
        label: 'Recebimento',
        path: '/receiving',
        icon: Truck,
        tcode: 'RC01',
        activeMatch: /^\/receiving(\/|$)/,
      },
      {
        id: 'expedicao',
        label: 'Expedição',
        path: '/shipping',
        icon: Truck,
        tcode: 'SH01',
        activeMatch: /^\/shipping(\/|$)/,
      },
      {
        id: 'yard',
        label: 'Yard (Parque & Docas)',
        path: '/yard',
        icon: MapPin,
        tcode: 'YD01',
        activeMatch: /^\/yard(\/|$)/,
      },
      {
        id: 'manufacturing',
        label: 'Copack',
        path: '/manufacturing/copack-orders',
        icon: Factory,
        tcode: 'CP01',
        activeMatch: /^\/manufacturing\/copack-orders(\/|$)/,
      },
    ],
  },

  // ============================== CADASTROS =============================
  {
    id: 'cadastros',
    label: 'CADASTROS',
    children: [
      {
        id: 'catalogo',
        label: 'Catálogo',
        icon: Package,
        tcode: 'BN',
        activeMatch: [
          /^\/catalog\/(uoms|products|product-packs|shelf-life-policies)(\/|$)/,
          /^\/warehouse\/3d(\/|$)/,
        ],
        children: [
          {
            id: 'uoms',
            label: 'Unidades (UOM)',
            path: '/catalog/uoms',
            tcode: 'BN01',
            activeMatch: /^\/catalog\/uoms(\/|$)/,
          },
          {
            id: 'products',
            label: 'Produtos',
            path: '/catalog/products',
            tcode: 'BN02',
            activeMatch: /^\/catalog\/products(\/|$)/,
          },
          {
            id: 'packs',
            label: 'Packs do Produto',
            path: '/catalog/product-packs',
            tcode: 'BN03',
            activeMatch: /^\/catalog\/product-packs(\/|$)/,
          },
          {
            id: 'shelf-life-policies',
            label: 'Políticas de shelf-life',
            path: '/catalog/shelf-life-policies',
            tcode: 'BN05',
            activeMatch: /^\/catalog\/shelf-life-policies(\/|$)/,
          },
          {
            id: 'warehouse-3d',
            label: 'Armazém 3D',
            path: '/warehouse/3d',
            icon: Boxes,
            tcode: 'BN04',
            activeMatch: /^\/warehouse\/3d(\/|$)/,
          },
        ],
      },
      {
        id: 'core',
        label: 'Estrutura (Core)',
        icon: Building2,
        tcode: 'CO',
        activeMatch: [/^\/core\/(companies|sites|warehouses|locations)(\/|$)/],
        children: [
          {
            id: 'companies',
            label: 'Empresas',
            path: '/core/companies',
            tcode: 'CO01',
            activeMatch: /^\/core\/companies(\/|$)/,
          },
          {
            id: 'sites',
            label: 'Sites',
            path: '/core/sites',
            tcode: 'CO02',
            activeMatch: /^\/core\/sites(\/|$)/,
          },
          {
            id: 'warehouses',
            label: 'Armazéns',
            path: '/core/warehouses',
            tcode: 'CO03',
            activeMatch: /^\/core\/warehouses(\/|$)/,
          },
          {
            id: 'locations',
            label: 'Endereços (Locations)',
            path: '/core/locations',
            tcode: 'CO04',
            activeMatch: /^\/core\/locations(\/|$)/,
          },
        ],
      },
      {
        id: 'partners',
        label: 'Parceiros',
        icon: Users,
        tcode: 'PT',
        activeMatch: [/^\/partners\/(customers|suppliers|carriers)(\/|$)/],
        children: [
          {
            id: 'customers',
            label: 'Clientes',
            path: '/partners/customers',
            tcode: 'PT01',
            activeMatch: /^\/partners\/customers(\/|$)/,
          },
          {
            id: 'suppliers',
            label: 'Fornecedores',
            path: '/partners/suppliers',
            tcode: 'PT02',
            activeMatch: /^\/partners\/suppliers(\/|$)/,
          },
          {
            id: 'carriers',
            label: 'Transportadoras',
            path: '/partners/carriers',
            tcode: 'PT03',
            activeMatch: /^\/partners\/carriers(\/|$)/,
          },
        ],
      },
    ],
  },

  // ============================== WORKFORCE =============================
  {
    id: 'workforce',
    label: 'WORKFORCE',
    children: [
      {
        id: 'wf-operators',
        label: 'Operadores',
        path: '/workforce/operators',
        icon: Users,
        tcode: 'WK01',
        activeMatch: [
          /^\/workforce\/operators(\/|$)/,
          /^\/workforce\/operators\/\d+\/skills(\/|$)/,
        ],
      },
      {
        id: 'wf-skills',
        label: 'Skills',
        path: '/workforce/skills',
        icon: Layers,
        tcode: 'WK02',
        activeMatch: /^\/workforce\/skills(\/|$)/,
      },
      {
        id: 'wf-parking',
        label: 'Parking Spots',
        path: '/workforce/parking-spots',
        icon: MapPin,
        tcode: 'WK03',
        activeMatch: /^\/workforce\/parking-spots(\/|$)/,
      },
      {
        id: 'wf-equip',
        label: 'Equipamentos',
        path: '/workforce/equipments',
        icon: Wrench,
        tcode: 'WK04',
        activeMatch: /^\/workforce\/equipments(\/|$)/,
      },
      {
        id: 'wf-devices',
        label: 'Coletores',
        path: '/workforce/devices',
        icon: Smartphone,
        tcode: 'WK05',
        activeMatch: /^\/workforce\/devices(\/|$)/,
      },
      {
        id: 'wf-shift-templates',
        label: 'Modelos de turno',
        path: '/workforce/shift-templates',
        icon: Calendar,
        tcode: 'WK06',
        activeMatch: /^\/workforce\/shift-templates(\/|$)/,
      },
      {
        id: 'wf-shift-assignments',
        label: 'Planeamento de turnos',
        path: '/workforce/shift-assignments',
        icon: Calendar,
        tcode: 'WK07',
        activeMatch: /^\/workforce\/shift-assignments(\/|$)/,
      },
      {
        id: 'wf-absences',
        label: 'Ausências / Férias',
        path: '/workforce/absences',
        icon: Calendar,
        tcode: 'WK08',
        activeMatch: /^\/workforce\/absences(\/|$)/,
      },
      {
        id: 'wf-work-accidents',
        label: 'Acidentes de trabalho',
        path: '/workforce/work-accidents',
        icon: AlertTriangle,
        tcode: 'WK09',
        activeMatch: /^\/workforce\/work-accidents(\/|$)/,
      },
      {
        id: 'wf-rejection-reasons',
        label: 'Motivos de indisponibilidade',
        path: '/workforce/rejection-reasons',
        icon: ListChecks,
        tcode: 'WK10',
        activeMatch: /^\/workforce\/rejection-reasons(\/|$)/,
      },
      {
        id: 'wf-risk-types',
        label: 'Matriz de risco',
        path: '/workforce/risk-types',
        icon: AlertTriangle,
        tcode: 'WK11',
        activeMatch: /^\/workforce\/risk-types(\/|$)/,
      },
      {
        id: 'tarefas',
        label: 'Tarefas',
        path: '/workforce/tasks',
        icon: Layers,
        tcode: 'WK12',
        activeMatch: /^\/workforce\/tasks(\/|$)/,
      },
    ],
  },

  // ============================== COMPRAS (PO) ==============================
  {
    id: 'purchasing',
    label: 'COMPRAS (PO)',
    icon: ShoppingCart,
    tcode: 'PO',
    activeMatch: [/^\/purchasing\/(orders|lines|queue)(\/|$)/],
    children: [
      {
        id: 'po-orders',
        label: 'Pedidos de compra',
        path: '/purchasing/orders',
        icon: List,
        tcode: 'PO01',
        activeMatch: /^\/purchasing\/orders(\/|$)/,
      },
      {
        id: 'po-lines',
        label: 'Linhas de compra',
        path: '/purchasing/lines',
        icon: Layers,
        tcode: 'PO02',
        activeMatch: /^\/purchasing\/lines(\/|$)/,
      },
      {
        id: 'po-queue',
        label: 'Fila de recebimento',
        path: '/purchasing/queue',
        icon: Inbox,
        tcode: 'PO03',
        activeMatch: /^\/purchasing\/queue(\/|$)/,
      },
    ],
  },

  // ============================== PRODUÇÃO (MES) ==============================
  {
    id: 'mes',
    label: 'PRODUÇÃO (MES)',
    icon: Factory,
    tcode: 'ME',
    activeMatch: [/^\/mes\/(orders|workcenters|terminal)(\/|$)/],
    children: [
      {
        id: 'mes-orders',
        label: 'Ordens de Produção',
        path: '/mes/orders',
        icon: List,
        tcode: 'ME01',
        activeMatch: /^\/mes\/orders(\/|$)/,
      },
      {
        id: 'mes-workcenters',
        label: 'Centros de Trabalho',
        path: '/mes/workcenters',
        icon: Factory,
        tcode: 'ME02',
        activeMatch: /^\/mes\/workcenters(\/|$)/,
      },
      {
        id: 'mes-terminal',
        label: 'Apontamento (Terminal)',
        path: '/mes/terminal',
        icon: ScanLine,
        tcode: 'ME03',
        activeMatch: /^\/mes\/terminal(\/|$)/,
      },
    ],
  },

  // ============================== MFC (embed) ==============================
  {
    id: 'mfc',
    label: 'MFC (Material Flow Control)',
    icon: Cpu,
    tcode: 'MF',
    activeMatch: [/^\/mfc(\/|$)/],
    children: [
      {
        id: 'mfc-dashboard',
        label: 'Dashboard',
        path: '/mfc/dashboard',
        icon: LayoutDashboard,
        tcode: 'MF01',
        activeMatch: /^\/mfc\/dashboard(\/|$)/,
      },
      {
        id: 'mfc-miniload',
        label: 'Miniload',
        path: '/mfc/miniload',
        icon: Boxes,
        tcode: 'MF02',
        activeMatch: /^\/mfc\/miniload(\/|$)/,
      },
      {
        id: 'mfc-scada',
        label: 'SCADA',
        path: '/mfc/scada',
        icon: Cpu,
        tcode: 'MF03',
        activeMatch: /^\/mfc\/scada(\/|$)/,
      },
      {
        id: 'mfc-missions',
        label: 'Missões',
        path: '/mfc/missions',
        icon: ListChecks,
        tcode: 'MF04',
        activeMatch: /^\/mfc\/missions(\/|$)/,
      },
      {
        id: 'mfc-equipment',
        label: 'Equipamentos',
        path: '/mfc/equipment',
        icon: Wrench,
        tcode: 'MF05',
        activeMatch: /^\/mfc\/equipment(\/|$)/,
      },
      {
        id: 'mfc-topology',
        label: 'Rotas / Topologia',
        path: '/mfc/topology',
        icon: Network,
        tcode: 'MF06',
        activeMatch: /^\/mfc\/topology(\/|$)/,
      },
      {
        id: 'mfc-events',
        label: 'Eventos',
        path: '/mfc/events',
        icon: Activity,
        tcode: 'MF07',
        activeMatch: /^\/mfc\/events(\/|$)/,
      },
      {
        id: 'mfc-adapters',
        label: 'Adapters',
        path: '/mfc/adapters',
        icon: Plug,
        tcode: 'MF08',
        activeMatch: /^\/mfc\/adapters(\/|$)/,
      },
      {
        id: 'mfc-rest-api',
        label: 'REST Vendor API',
        path: '/mfc/rest-api',
        icon: Globe,
        tcode: 'MF09',
        activeMatch: /^\/mfc\/rest-api(\/|$)/,
      },
      {
        id: 'mfc-opc-ua',
        label: 'OPC UA',
        path: '/mfc/opc-ua',
        icon: Radio,
        tcode: 'MF10',
        activeMatch: /^\/mfc\/opc-ua(\/|$)/,
      },
      {
        id: 'mfc-vda5050',
        label: 'VDA 5050 / AMR',
        path: '/mfc/vda5050',
        icon: Bot,
        tcode: 'MF11',
        activeMatch: /^\/mfc\/vda5050(\/|$)/,
      },
      {
        id: 'mfc-plc-tags',
        label: 'PLC Tags',
        path: '/mfc/plc-tags',
        icon: Tag,
        tcode: 'MF12',
        activeMatch: /^\/mfc\/plc-tags(\/|$)/,
      },
      {
        id: 'mfc-snapshot',
        label: 'Snapshot',
        path: '/mfc/snapshot',
        icon: Camera,
        tcode: 'MF13',
        activeMatch: /^\/mfc\/snapshot(\/|$)/,
      },
      {
        id: 'mfc-replay',
        label: 'Replay',
        path: '/mfc/replay',
        icon: History,
        tcode: 'MF14',
        activeMatch: /^\/mfc\/replay(\/|$)/,
      },
      {
        id: 'mfc-wms-mes',
        label: 'WMS / MES',
        path: '/mfc/wms-mes',
        icon: Factory,
        tcode: 'MF15',
        activeMatch: /^\/mfc\/wms-mes(\/|$)/,
      },
      {
        id: 'mfc-persistence',
        label: 'Persistência',
        path: '/mfc/persistence',
        icon: Database,
        tcode: 'MF16',
        activeMatch: /^\/mfc\/persistence(\/|$)/,
      },
      {
        id: 'mfc-scenarios',
        label: 'Cenários',
        path: '/mfc/scenarios',
        icon: FlaskConical,
        tcode: 'MF17',
        activeMatch: /^\/mfc\/scenarios(\/|$)/,
      },
      {
        id: 'mfc-fault-injection',
        label: 'Fault Injection',
        path: '/mfc/fault-injection',
        icon: AlertTriangle,
        tcode: 'MF18',
        activeMatch: /^\/mfc\/fault-injection(\/|$)/,
      },
      {
        id: 'mfc-health',
        label: 'Health',
        path: '/mfc/health',
        icon: HeartPulse,
        tcode: 'MF19',
        activeMatch: /^\/mfc\/health(\/|$)/,
      },
      {
        id: 'mfc-api-v1',
        label: 'API v1',
        path: '/mfc/api-v1',
        icon: Code2,
        tcode: 'MF20',
        activeMatch: /^\/mfc\/api-v1(\/|$)/,
      },
    ],
  },

  // ========================= CATÁLOGO GLOBAL (embed) =========================
  // Distinto do catálogo local BN (cadastro do cliente): CG é o catálogo global
  // de produtos (GTIN/EAN-13) embebido de catalog.movaticwms.com.
  {
    id: 'catalog',
    label: 'Catálogo Global',
    icon: Barcode,
    tcode: 'CG',
    activeMatch: [/^\/catalog$/],
    children: [
      {
        id: 'catalog-global-consult',
        label: 'Consulta Global',
        path: '/catalog',
        icon: Barcode,
        tcode: 'CG01',
        activeMatch: /^\/catalog$/,
      },
    ],
  },

  // ============================== HUB (embed) ==============================
  // Observabilidade do Hub de integração (ADR-0010), dashboard embebido de
  // hub.movaticwms.com. Distinto do MFC e do Catálogo Global.
  {
    id: 'hub',
    label: 'Hub',
    icon: Activity,
    tcode: 'HB',
    activeMatch: [/^\/hub$/],
    children: [
      {
        id: 'hub-observability',
        label: 'Observabilidade',
        path: '/hub',
        icon: Network,
        tcode: 'HB01',
        activeMatch: /^\/hub$/,
      },
    ],
  },

  // ============================== MPC (embed) ==============================
  // Movatic GP — Gestão de Projetos, embebida por iframe de mpc.movaticwms.com.
  // Mesmo molde do Hub/Catálogo Global (ADR-0052). Distinto do MFC.
  {
    id: 'mpc',
    label: 'Gestão de Projetos',
    icon: ListChecks,
    tcode: 'GP',
    activeMatch: [/^\/mpc$/],
    children: [
      {
        id: 'mpc-projects',
        label: 'Projetos',
        path: '/mpc',
        icon: ListChecks,
        tcode: 'GP01',
        activeMatch: /^\/mpc$/,
      },
    ],
  },

  // =============================== FRETE ===============================
  {
    id: 'pricing',
    label: 'FRETE',
    icon: Truck,
    tcode: 'FR',
    activeMatch: [/^\/pricing\/(rate-tables|quote|surcharges)(\/|$)/],
    children: [
      {
        id: 'pricing-rate-tables',
        label: 'Tabelas de Frete',
        path: '/pricing/rate-tables',
        icon: List,
        tcode: 'FR01',
        activeMatch: /^\/pricing\/rate-tables(\/|$)/,
      },
      {
        id: 'pricing-quote',
        label: 'Simulador de Frete',
        path: '/pricing/quote',
        icon: Receipt,
        tcode: 'FR02',
        activeMatch: /^\/pricing\/quote(\/|$)/,
      },
    ],
  },

  // ========================= ASSEMBLY (RASTREIO) ========================
  {
    id: 'assembly',
    label: 'ASSEMBLY',
    icon: Wrench,
    tcode: 'AS',
    activeMatch: [/^\/assembly\/(assets|links)(\/|$)/],
    children: [
      {
        id: 'assembly-assets',
        label: 'Assets (unidades seriadas)',
        path: '/assembly/assets',
        icon: Layers,
        tcode: 'AS01',
        activeMatch: /^\/assembly\/assets(\/|$)/,
      },
      {
        id: 'assembly-links',
        label: 'Ligações de montagem',
        path: '/assembly/links',
        icon: ListChecks,
        tcode: 'AS02',
        activeMatch: /^\/assembly\/links(\/|$)/,
      },
    ],
  },

  // ========================= MARKETING (CAMPANHAS) ======================
  {
    id: 'marketing',
    label: 'MARKETING',
    icon: Megaphone,
    tcode: 'MK',
    activeMatch: [/^\/marketing\/(campaigns|campaign-products)(\/|$)/],
    children: [
      {
        id: 'mkt-campaigns',
        label: 'Campanhas de marketing',
        path: '/marketing/campaigns',
        icon: Megaphone,
        tcode: 'MK01',
        activeMatch: /^\/marketing\/campaigns(\/|$)/,
      },
      {
        id: 'mkt-campaign-products',
        label: 'Produtos de campanha',
        path: '/marketing/campaign-products',
        icon: TrendingUp,
        tcode: 'MK02',
        activeMatch: /^\/marketing\/campaign-products(\/|$)/,
      },
    ],
  },

  // ========================= ORDERS (SALES ORDERS) ======================
  {
    id: 'orders-group',
    label: 'Orders (Sales Orders)',
    tcode: 'OD',
    activeMatch: [/^\/orders\/(orders|lines)(\/|$)/],
    children: [
      {
        id: 'orders-list',
        label: 'Pedidos de venda',
        path: '/orders/orders',
        icon: Receipt,
        tcode: 'OD01',
        activeMatch: [/^\/orders\/orders(\/.*)?$/],
      },
      {
        id: 'orders-lines',
        label: 'Linhas de pedido',
        path: '/orders/lines',
        icon: ListChecks,
        tcode: 'OD02',
        activeMatch: [/^\/orders\/lines(\/.*)?$/],
      },
    ],
  },

  // =============================== SISTEMA ==============================
  {
    id: 'sistema',
    label: 'SISTEMA',
    children: [
      {
        id: 'configuracoes',
        label: 'Configurações',
        path: '/settings',
        icon: Settings,
        tcode: 'SY01',
        activeMatch: /^\/settings(\/|$)/,
      },
      {
        id: 'usuarios',
        label: 'Usuários',
        path: '/workforce/users',
        icon: Users,
        tcode: 'SY02',
        activeMatch: [
          /^\/workforce\/users(\/|$)/,
          /^\/workforce\/users\/\d+\/skills(\/|$)/,
        ],
      },
    ],
  },

  // ========================= MOVATIC DEV (DEMO/TESTE) ===================
  {
    id: 'movatic-dev',
    label: 'MOVATIC DEV',
    icon: FlaskConical,
    tcode: 'DV',
    superuserOnly: true,
    activeMatch: [/^\/movatic-dev(\/|$)/],
    children: [
      {
        id: 'movatic-dev-create',
        label: 'Criar ordem',
        path: '/movatic-dev/criar',
        icon: ShoppingCart,
        tcode: 'DV01',
        activeMatch: /^\/movatic-dev\/criar/,
      },
      {
        id: 'movatic-dev-catalog',
        label: 'Catálogo',
        path: '/movatic-dev',
        icon: Package,
        tcode: 'DV02',
        activeMatch: /^\/movatic-dev(\/|$)/,
      },
    ],
  },
]

export default MENUS
